// React
import { useState, useCallback } from 'react'

// Components
import { Sidebar } from './components'
import { BaseCard, BaseImage, BaseLabel, BaseEmpty, SharedQuestionSetTable } from 'components'

// Mobx
import { observer } from 'mobx-react'

// Styles
import styles from './index.module.scss'

// Antd
import { Button, Spin } from 'antd'
import { WarningOutlined } from '@ant-design/icons'

// i18n
import { useTranslation } from 'react-i18next'

// Interfaces
import { ISharedCompetencyUnitDetailProps } from './interfaces'

// Lodash
import isEmpty from 'lodash/isEmpty'

// Constant
import { ASSESSMENT_FEATURES } from 'constant/shared.constant'

// React Router Dom
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { FeedbackNotes } from 'pages/back-office/assessment/AssessmentDetail/components/Assessment/DetailAssessment/components/FeedbackNotes/FeedbackNotes'

const SharedCompetencyUnitDetail = observer(
  ({
    competencyUnits,
    competencyUnitDetail,
    selectedCompetencyUnitId,
    isSidebarLoading,
    isDetailLoading,
    onRedirectBack,
    onUpdateUserAnswer,
    onUploadDocument,
    onDeleteDocument,
    onAddFileItem,
    onDeleteFileItem,
    onDownloadDocument,
    isReadOnly,
    isFromAssessment,
  }: ISharedCompetencyUnitDetailProps) => {
    // Hook
    const { t } = useTranslation()
    const params = useParams<{ competencyUnitId: string; assessmentId: string }>()
    const history = useHistory()
    const location = useLocation()
    const [toggleSidebar, setToggleSidebar] = useState<boolean>(false)

    /**
     * @description move to the next competency unit
     *
     * @return {void} void
     */
    const onClickNextButton = useCallback(() => {
      const temp = competencyUnits.findIndex(unit => unit.competencyUnitId === parseInt(params.competencyUnitId))
      if (temp !== competencyUnits.length - 1) {
        history.push(`${location.pathname.replace(/\/[0-9]+$/, '/' + competencyUnits[temp + 1].competencyUnitId?.toString() || '')}`)
      }
      else onRedirectBack()
    }, [competencyUnits, params, history, location.pathname, onRedirectBack])

    /**
     * @description move to the prev competency unit
     *
     * @return {void} void
     */
    const onClickPrevButton = useCallback(() => {
      const temp = competencyUnits.findIndex(unit => unit.competencyUnitId === parseInt(params.competencyUnitId))
      if (temp !== 0) {
        history.push(`${location.pathname.replace(/\/[0-9]+$/, '/' + competencyUnits[temp - 1].competencyUnitId?.toString() || '')}`)
      }
    }, [competencyUnits, params, history, location.pathname])

    return (
      <div className="webpage-body lg:py-10 lg:px-10">
        {/* Sidebar */}
        <Sidebar
          competencyUnits={competencyUnits}
          isLoading={isSidebarLoading}
          selectedCompetencyUnitId={selectedCompetencyUnitId}
          isFromAssessment={isFromAssessment}
          isSidebarOpen={toggleSidebar}
          setToggleSidebar={setToggleSidebar}
        />

        {/* Body */}
        <div className={toggleSidebar ? styles.open : styles.close}>
          <div className="flex flex-col gap-2 lg:flex-row items-start">
            <div className="cursor-pointer" onClick={onRedirectBack}>
              <div className="flex items-center gap-2">
                <BaseImage isIcon icon="chevron-left" width="7.19px" height="12.5px" />
                <BaseLabel label={t('app.back')} className="text-primary font-12 lg:font-14" />
              </div>
            </div>
            <div className="flex flex-col justify-center text-center items-center w-full gap-1 mb-4">
              {competencyUnitDetail.competencyUnitName ? (
                <BaseLabel
                  label={competencyUnitDetail.competencyUnitName || ''}
                  className="font-16 lg:font-24 text-grey-font font-weight-700"
                />
              ) : (
                '-'
              )}
              {competencyUnitDetail.competencyUnitTitle && competencyUnitDetail.competencyUnitCode ? (
                <BaseLabel
                  label={`${competencyUnitDetail.competencyUnitTitle} (${competencyUnitDetail.competencyUnitCode})`}
                  className="font-16 lg:font-24 text-primary font-weight-700"
                />
              ) : (
                '-'
              )}
            </div>
          </div>

          <Spin spinning={isDetailLoading}>
            <BaseCard
              alert={
                <div className="flex flex-col lg:flex-row items-center gap-3 mb-4">
                  <WarningOutlined style={{ color: '#DC6900', fontSize: 16 }} />

                  {/* Title */}
                  <BaseLabel
                    label={`${t('shared.assessment.selfAssessment.alert')}:`}
                    className="font-14 lg:font-16 text-center"
                  />
                </div>
              }
            >
              {!isEmpty(competencyUnitDetail.questionSet) ? (
                competencyUnitDetail.questionSet.map(qSet => {
                  return qSet.groupData.map(qGroup => (
                    <SharedQuestionSetTable
                      questionSetId={qSet.questionSetId || 0}
                      key={qGroup.qGroupid}
                      data={qGroup}
                      onUpdateUserAnswer={onUpdateUserAnswer}
                      onUploadDocument={onUploadDocument}
                      onDeleteDocument={onDeleteDocument}
                      onAddFileItem={onAddFileItem}
                      onDeleteFileItem={onDeleteFileItem}
                      onDownloadDocument={onDownloadDocument}
                      isReadOnly={isReadOnly}
                      featureName={ASSESSMENT_FEATURES.SELF_ASSESSMENT}
                    />
                  ))
                })
              ) : (
                <BaseEmpty />
              )}
              <div className='flex flex-row space-x-4 justify-end my-8 mb-5'>
                <Button type='link' className='custom-button' style={{
                  color: '#A8A8A8'
                }} onClick={onClickPrevButton} disabled={competencyUnits.findIndex(unit => unit.competencyUnitId === parseInt(params.competencyUnitId)) === 0}>
                  {t('app.prev')}
                </Button>
                <Button type='primary' className='custom-button' style={{
                  paddingLeft: 30,
                  paddingRight: 30
                }} onClick={onClickNextButton}>
                  {
                    competencyUnits.length - 1 === competencyUnits.findIndex(unit => unit.competencyUnitId === parseInt(params.competencyUnitId)) ? t('app.finish') : t('app.next')
                  }
                </Button>
              </div>
            </BaseCard>
          </Spin>

          {/* Delete Feedback Notes Registration here tachul */}
        </div>
      </div>
    )
  },
)

export { SharedCompetencyUnitDetail }